<template>
  <div class="plot-draw-panel h-100 d-flex flex-column">
    <div class="plot-tool-section mb-3">
      <div class="d-flex align-center justify-space-between mb-2">
        <div class="text-subtitle-1 font-weight-medium">地图工具</div>
        <v-btn small color="primary" @click="backToInsureApply">
          返回投保
        </v-btn>
      </div>
      <div class="tool-grid mb-2">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn :color="mode === 'select' ? 'primary' : ''" outlined small class="tool-btn" @click="startSelect" v-bind="attrs" v-on="on">
              <v-icon small>mdi-cursor-default</v-icon>
            </v-btn>
          </template>
          <span>选择</span>
        </v-tooltip>

        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn :color="mode === 'box-select' ? 'primary' : ''" outlined small class="tool-btn" @click="startBoxSelect" v-bind="attrs" v-on="on">
              <v-icon small>mdi-selection-drag</v-icon>
            </v-btn>
          </template>
          <span>框选</span>
        </v-tooltip>

        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn :color="mode === 'add' ? 'primary' : ''" outlined small class="tool-btn" @click="startDraw" v-bind="attrs" v-on="on">
              <v-icon small>mdi-plus-box-outline</v-icon>
            </v-btn>
          </template>
          <span>新增地块</span>
        </v-tooltip>

        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn :color="mode === 'modify' ? 'primary' : ''" outlined small class="tool-btn" @click="startModify" v-bind="attrs" v-on="on" :disabled="!isEditable && !!currentFeature && mode !== 'modify'">
              <v-icon small>mdi-square-edit-outline</v-icon>
            </v-btn>
          </template>
          <span>修改地块</span>
        </v-tooltip>

        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn :color="mode === 'clip' ? 'primary' : ''" outlined small class="tool-btn" @click="startClip" v-bind="attrs" v-on="on">
              <v-icon small>mdi-scissors-cutting</v-icon>
            </v-btn>
          </template>
          <span>裁剪地块</span>
        </v-tooltip>

        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn outlined small class="tool-btn" color="primary" @click="mergeSelected" v-bind="attrs" v-on="on">
              <v-icon small>mdi-layers-outline</v-icon>
            </v-btn>
          </template>
          <span>合并地块</span>
        </v-tooltip>

        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn outlined small class="tool-btn" color="error" @click="deleteSelected" v-bind="attrs" v-on="on" :disabled="!isEditable && !!currentFeature">
              <v-icon small>mdi-delete-outline</v-icon>
            </v-btn>
          </template>
          <span>删除地块</span>
        </v-tooltip>
      </div>

      <div class="d-flex align-center">
        <span class="text-body-2 mr-2">捕捉点</span>
        <v-switch v-model="snapEnabled" dense inset hide-details @change="toggleSnap"></v-switch>
      </div>
    </div>

    <div class="plot-tool-section flex-grow-1">
      <div class="d-flex justify-space-between align-center mb-2">
        <div class="text-subtitle-1 font-weight-medium">地块属性</div>
        <div class="text-caption grey--text" v-if="form.plotCode" :title="form.plotCode">
          ID: {{ formatPlotCode(form.plotCode) }}
        </div>
      </div>
      <v-text-field v-model="form.plotName" label="地块名称" dense outlined :disabled="!isEditable"></v-text-field>
      <v-text-field v-model="form.ownerName" label="所属人" dense outlined :disabled="!isEditable"></v-text-field>
      <v-text-field v-model="form.idCard" label="身份证号" dense outlined :disabled="!isEditable"></v-text-field>
      <v-text-field v-model="form.areaMu" label="面积(亩)" dense outlined readonly placeholder="自动计算"></v-text-field>
      <v-textarea v-model="form.remark" label="备注" dense outlined rows="3" placeholder="可输入地块备注信息" :disabled="!isEditable"></v-textarea>
      <div class="d-flex justify-end mt-2">
        <v-btn text class="mr-2" @click="cancelEdit">取消</v-btn>
        <v-btn color="primary" @click="savePlot" :disabled="!isEditable && !!currentFeature">保存地块</v-btn>
      </div>
    </div>

    <v-snackbar v-model="snackbar" top>
      {{ snackbarText }}
    </v-snackbar>
  </div>
</template>

<script>
import axios from '@/axios'
import Feature from 'ol/Feature'
import Draw from 'ol/interaction/Draw'
import Modify from 'ol/interaction/Modify'
import Select from 'ol/interaction/Select'
import Snap from 'ol/interaction/Snap'
import DragBox from 'ol/interaction/DragBox'
import VectorLayer from 'ol/layer/Vector'
import VectorSource from 'ol/source/Vector'
import { Style, Stroke, Fill } from 'ol/style'
import GeoJSON from 'ol/format/GeoJSON'
import { shiftKeyOnly, never } from 'ol/events/condition'
import { unByKey } from 'ol/Observable'
import * as turf from '@turf/turf'
import { PLOT_WMS_LAYER, PLOT_WMS_STYLES } from '@/const/const.js'
import { buildPlotStatusCql } from '@/util/plotFilters'
import { buildPlotWmsSldBody } from '@/util/PlotWmsSld'

export default {
  name: 'PlotDrawPanel',
  props: {
    mapRef: {
      type: Object,
      default: null
    },
    mapShowInsuredPlots: {
      type: Boolean,
      default: true
    },
    mapShowUninsuredPlots: {
      type: Boolean,
      default: true
    },
    mapShowBoundaryOnly: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      initialized: false,
      editSource: null,
      editLayer: null,
      helperSource: null,
      helperLayer: null,
      drawInteraction: null,
      modifyInteraction: null,
      selectInteraction: null,
      snapInteraction: null,
      dragBoxInteraction: null,
      mapClickListener: null,
      boxSelectAppend: false,
      snapEnabled: false,
      mode: 'idle',
      currentFeature: null,
      form: {
        plotName: '',
        ownerName: '',
        idCard: '',
        remark: '',
        areaMu: '',
        plotCode: ''
      },
      snackbar: false,
      snackbarText: '',
      showInsuredPlots: this.mapShowInsuredPlots,
      showUninsuredPlots: this.mapShowUninsuredPlots,
      showBoundaryOnly: this.mapShowBoundaryOnly
    }
  },
  computed: {
    isEditable () {
      if (!this.currentFeature) return false
      const id = this.currentFeature.get('id')
      if (!id) return true
      const code = this.currentFeature.get('external_plot_code')
      return !code || code.startsWith('PLT')
    },
    selectedArea () {
      return this.$store?.state?.selectedArea || null
    }
  },
  watch: {
    mapRef: {
      immediate: true,
      handler (map) {
        if (map && !this.initialized) {
          this.bootstrapWithMap(map)
        }
      }
    },
    selectedArea (newVal, oldVal) {
      if (newVal && newVal !== oldVal && this.initialized) {
        this.loadPlots()
      }
    },
    mapShowInsuredPlots (value) {
      this.showInsuredPlots = value
      this.applyFilterStateToMap()
    },
    mapShowUninsuredPlots (value) {
      this.showUninsuredPlots = value
      this.applyFilterStateToMap()
    },
    mapShowBoundaryOnly (value) {
      this.showBoundaryOnly = value
      this.applyFilterStateToMap()
    }
  },
  beforeDestroy () {
    this.persistPlotDrawState()
    this.teardownMapBindings()
  },
  methods: {
    backToInsureApply () {
      this.$emit('request-search-mode')
    },
    bootstrapWithMap (map) {
      if (!map || this.initialized) return
      this.initialized = true
      this.initLayers()
      this.initInteractions()
      this.setupMapClickListener()
      this.loadPlots()
      this.restorePlotDrawState()
      this.applyFilterStateToMap()
    },
    teardownMapBindings () {
      const map = this.mapRef
      this.cleanupInteractions()
      if (map && this.mapClickListener) {
        map.un('singleclick', this.mapClickListener)
      }
      this.mapClickListener = null
      if (map && this.editLayer) {
        map.removeLayer(this.editLayer)
      }
      if (map && this.helperLayer) {
        map.removeLayer(this.helperLayer)
      }
      this.editLayer = null
      this.helperLayer = null
      this.editSource = null
      this.helperSource = null
      this.currentFeature = null
      this.initialized = false
    },
    getCurrentUserId () {
      const user = this.$store?.state?.user
      return user?.id || user?.user_id || user?.username || user?.email || null
    },
    getCurrentAreaCode () {
      return this.selectedArea?.place_id || this.selectedArea?.code || this.$store.state.user?.bound_region_code || null
    },
    getFeaturePersistKey (feature) {
      if (!feature) return null
      const featureId = feature.get('id') || feature.getId()
      if (featureId !== undefined && featureId !== null && featureId !== '') {
        return `id:${String(featureId)}`
      }
      const plotCode = feature.get('external_plot_code')
      if (plotCode) {
        return `code:${String(plotCode)}`
      }
      return null
    },
    findFeatureByPersistKey (key) {
      if (!key || !this.editSource) return null
      const [prefix, rawValue] = String(key).split(':')
      if (!prefix || !rawValue) return null

      const features = this.editSource.getFeatures()
      if (prefix === 'id') {
        return features.find((feature) => {
          const featureId = feature.get('id') || feature.getId()
          return featureId != null && String(featureId) === rawValue
        }) || null
      }

      if (prefix === 'code') {
        return features.find((feature) => String(feature.get('external_plot_code') || '') === rawValue) || null
      }

      return null
    },
    exportDraftFeaturesGeoJson () {
      if (!this.editSource || !this.mapRef) return []
      const geojson = new GeoJSON()
      const draftFeatures = this.editSource.getFeatures().filter((feature) => !this.getFeaturePersistKey(feature))
      return draftFeatures.map((feature) => geojson.writeFeatureObject(feature, {
        dataProjection: 'EPSG:4326',
        featureProjection: this.mapRef.getView().getProjection()
      }))
    },
    importDraftFeaturesGeoJson (features) {
      if (!Array.isArray(features) || features.length === 0 || !this.editSource || !this.mapRef) return
      const geojson = new GeoJSON()
      features.forEach((featureObject) => {
        if (!featureObject || !featureObject.geometry) return
        try {
          const feature = geojson.readFeature(featureObject, {
            dataProjection: 'EPSG:4326',
            featureProjection: this.mapRef.getView().getProjection()
          })
          this.editSource.addFeature(feature)
        } catch (error) {
          console.warn('Failed to restore draft feature:', error)
        }
      })
    },
    buildPlotDrawState () {
      const selectedKeys = this.selectInteraction
        ? this.selectInteraction.getFeatures().getArray().map((feature) => this.getFeaturePersistKey(feature)).filter(Boolean)
        : []
      const currentFeatureKey = this.getFeaturePersistKey(this.currentFeature)

      let mapView = null
      if (this.mapRef) {
        const view = this.mapRef.getView()
        mapView = {
          center: view.getCenter(),
          zoom: view.getZoom(),
          rotation: view.getRotation()
        }
      }

      return {
        version: 2,
        savedAt: Date.now(),
        userId: this.getCurrentUserId(),
        areaCode: this.getCurrentAreaCode(),
        fromRoute: this.$route?.path || '/insure-apply',
        toRoute: null,
        mode: this.mode,
        snapEnabled: !!this.snapEnabled,
        showInsuredPlots: !!this.showInsuredPlots,
        showUninsuredPlots: !!this.showUninsuredPlots,
        form: { ...this.form },
        currentFeatureKey,
        selectedFeatureKeys: selectedKeys,
        draftFeaturesGeoJson: this.exportDraftFeaturesGeoJson(),
        mapView
      }
    },
    persistPlotDrawState (extra = {}) {
      const payload = {
        ...this.buildPlotDrawState(),
        ...extra
      }
      this.$store.commit('setPlotDrawState', payload)
    },
    restorePlotDrawState () {
      const saved = this.$store.state.plotDrawState
      if (!saved || typeof saved !== 'object') return

      const currentUserId = this.getCurrentUserId()
      if (saved.userId) {
        if (!currentUserId || saved.userId !== currentUserId) {
          return
        }
      }

      const currentAreaCode = this.getCurrentAreaCode()
      if (saved.areaCode && saved.areaCode !== currentAreaCode) {
        return
      }

      this.snapEnabled = !!saved.snapEnabled
      this.showInsuredPlots = this.mapShowInsuredPlots
      this.showUninsuredPlots = this.mapShowUninsuredPlots
      this.showBoundaryOnly = this.mapShowBoundaryOnly
      this.form = {
        ...this.form,
        ...(saved.form && typeof saved.form === 'object' ? saved.form : {})
      }

      this.importDraftFeaturesGeoJson(saved.draftFeaturesGeoJson)

      const mode = saved.mode || 'idle'
      this.startSelect()

      this.$nextTick(() => {
        if (!this.editSource || !this.selectInteraction) return
        const selected = this.selectInteraction.getFeatures()
        selected.clear()

        if (Array.isArray(saved.selectedFeatureKeys)) {
          saved.selectedFeatureKeys.forEach((key) => {
            const feature = this.findFeatureByPersistKey(key)
            if (feature) selected.push(feature)
          })
        }

        if (saved.currentFeatureKey) {
          const current = this.findFeatureByPersistKey(saved.currentFeatureKey)
          if (current) {
            this.currentFeature = current
            this.updateFormFromFeature(current)
          }
        }

        if (mode === 'box-select') {
          this.startBoxSelect()
        } else if (mode === 'add') {
          this.startDraw()
        } else if (mode === 'modify') {
          if (selected.getLength() > 0) {
            this.startModify()
          }
        } else if (mode === 'clip') {
          if (selected.getLength() === 1) {
            this.startClip()
          }
        } else if (mode === 'idle') {
          this.mode = 'idle'
          this.cleanupInteractions()
        }

        if (saved.mapView && this.mapRef) {
          const view = this.mapRef.getView()
          if (Array.isArray(saved.mapView.center)) {
            view.setCenter(saved.mapView.center)
          }
          if (saved.mapView.zoom !== undefined && saved.mapView.zoom !== null) {
            view.setZoom(saved.mapView.zoom)
          }
          if (saved.mapView.rotation !== undefined && saved.mapView.rotation !== null) {
            view.setRotation(saved.mapView.rotation)
          }
        }
      })
    },
    formatPlotCode (code) {
      if (!code || code.length <= 20) return code
      return `${code.substring(0, 10)}...${code.substring(code.length - 8)}`
    },
    initLayers () {
      if (!this.mapRef || this.editLayer) return

      this.editSource = new VectorSource()
      this.editLayer = new VectorLayer({
        source: this.editSource,
        style: (feature) => {
          const bsValue = String(feature.get('bs') ?? '').trim()
          const insured = bsValue === '1' || bsValue === '1.0' || bsValue === '1.00'

          if (insured && !this.showInsuredPlots) return null
          if (!insured && !this.showUninsuredPlots) return null

          return new Style({
            fill: new Fill({ color: insured ? 'rgba(253, 216, 53, 0.45)' : 'rgba(102, 187, 106, 0.35)' }),
            stroke: new Stroke({ color: insured ? '#FBC02D' : '#388E3C', width: 2 })
          })
        },
        zIndex: 200
      })
      this.editLayer.set('lid', 'plot-draw-layer')
      this.mapRef.addLayer(this.editLayer)

      this.helperSource = new VectorSource()
      this.helperLayer = new VectorLayer({
        source: this.helperSource,
        style: new Style({
          stroke: new Stroke({ color: '#FF7043', width: 2, lineDash: [8, 6] })
        }),
        zIndex: 201
      })
      this.helperLayer.set('lid', 'plot-draw-helper')
      this.mapRef.addLayer(this.helperLayer)
    },
    updateWmsFilter (villageCode) {
      if (!this.mapRef) return

      const layers = this.mapRef.getLayers().getArray()
      const wmsLayer = layers.find(l => l.get('lid') === 'wms-plot-layer')

      if (wmsLayer) {
        const source = wmsLayer.getSource()
        if (source && typeof source.updateParams === 'function') {
          let filter = '1=0'
          if (villageCode) {
            let villageFilter = ''
            if (String(villageCode).includes(',')) {
              const codes = String(villageCode).split(',').map(c => `'${c.trim()}'`).join(',')
              villageFilter = `village_code IN (${codes})`
            } else {
              villageFilter = `village_code='${villageCode}'`
            }
            const statusFilter = this.buildPlotStatusFilter()
            filter = `${villageFilter} AND ${statusFilter} AND (hidden IS NULL OR hidden <> 1)`
          }
          const styleParams = this.buildPlotWmsStyleParams()
          source.updateParams({
            CQL_FILTER: filter,
            ...styleParams
          })
          if (typeof source.refresh === 'function') {
            source.refresh()
          }
        }
      }
    },
    buildPlotStatusFilter () {
      return buildPlotStatusCql(this.showInsuredPlots, this.showUninsuredPlots)
    },
    buildPlotWmsStyleParams () {
      if (this.showBoundaryOnly) {
        return {
          STYLES: PLOT_WMS_STYLES.MAP_BOUNDARY_ONLY,
          SLD_BODY: buildPlotWmsSldBody(PLOT_WMS_LAYER, {
            styleType: 'single',
            boundaryOnly: true
          })
        }
      }

      return {
        STYLES: PLOT_WMS_STYLES.INSURE_APPLY,
        SLD_BODY: buildPlotWmsSldBody(PLOT_WMS_LAYER, {
          styleType: 'status',
          showInsured: this.showInsuredPlots,
          showUninsured: this.showUninsuredPlots,
          highlightUninsuredWater: true
        })
      }
    },
    togglePlotStatus (type) {
      if (type === 'insured') {
        this.showInsuredPlots = !this.showInsuredPlots
      }

      if (type === 'uninsured') {
        this.showUninsuredPlots = !this.showUninsuredPlots
      }

      const villageCode = this.$store.state.user?.bound_region_code || this.selectedArea?.place_id
      this.updateWmsFilter(villageCode)

      if (this.editLayer) {
        this.editLayer.changed()
      }
    },
    applyFilterStateToMap () {
      if (!this.initialized) return
      const villageCode = this.$store.state.user?.bound_region_code || this.selectedArea?.place_id
      this.updateWmsFilter(villageCode)
      if (this.editLayer) {
        this.editLayer.changed()
      }
    },
    async loadPlots () {
      if (!this.mapRef || !this.editSource) {
        return
      }

      const villageCode = this.$store.state.user?.bound_region_code || this.selectedArea?.place_id
      this.updateWmsFilter(villageCode)

      if (!villageCode) {
        this.editSource.clear()
        this.setSelectedFeature(null)
        return
      }

      try {
        const response = await axios.get('/plots/', {
          params: {
            village_code: villageCode,
            limit: 10000
          }
        })

        const geojsonFormat = new GeoJSON()
        const features = geojsonFormat.readFeatures(response.data, {
          featureProjection: this.mapRef.getView().getProjection()
        })

        features.forEach((feature) => {
          const featureId = feature.getId()
          if (featureId != null && feature.get('id') == null) {
            feature.set('id', featureId)
          }
        })

        this.editSource.clear()
        this.editSource.addFeatures(features)
        this.setSelectedFeature(null)
      } catch (error) {
        const errorMsg = error.response?.data?.detail || '地块加载失败'
        this.showMessage(errorMsg)
      }
    },
    initInteractions () {
      if (!this.mapRef || !this.editLayer) return

      if (!this.selectInteraction) {
        this.selectInteraction = new Select({
          condition: never,
          toggleCondition: shiftKeyOnly,
          layers: [this.editLayer],
          multi: true
        })
        this.selectInteraction.on('select', () => {
          const selected = this.selectInteraction.getFeatures()
          const selectedCount = selected.getLength()
          this.currentFeature = selectedCount === 1 ? selected.item(0) : null
          this.updateFormFromFeature(this.currentFeature)

          if (selectedCount > 1) {
            this.showMessage(`已选择 ${selectedCount} 个地块`)
          }
        })
        this.mapRef.addInteraction(this.selectInteraction)
      }
    },
    setupMapClickListener () {
      if (!this.mapRef) return

      if (this.mapClickListener) {
        this.mapRef.un('singleclick', this.mapClickListener)
      }

      this.mapClickListener = async (evt) => {
        if (this.mode !== 'select' && this.mode !== 'box-select') return

        const shiftPressed = evt.originalEvent.shiftKey

        const pixel = this.mapRef.getEventPixel(evt.originalEvent)
        const localFeature = this.mapRef.forEachFeatureAtPixel(pixel, (feature, layer) => {
          if (!layer) return null
          const lid = layer.get('lid')
          if (lid === 'plot-draw-layer') return feature
          return null
        })

        if (localFeature) {
          this.applySelection(localFeature, shiftPressed)
          return
        }

        const viewResolution = this.mapRef.getView().getResolution()
        const projection = this.mapRef.getView().getProjection()
        const wmsLayer = this.mapRef.getLayers().getArray().find(layer => layer.get('lid') === 'wms-plot-layer')
        if (!wmsLayer) {
          return
        }

        const source = wmsLayer.getSource()
        const url = source.getFeatureInfoUrl(evt.coordinate, viewResolution, projection, {
          INFO_FORMAT: 'application/json',
          FEATURE_COUNT: 1
        })

        if (!url) return

        try {
          const response = await fetch(url)
          const data = await response.json()

          if (data.features && data.features.length > 0) {
            const feature = data.features[0]
            const bsValue = String(feature.properties?.bs ?? '').trim()
            const insured = bsValue === '1' || bsValue === '1.0' || bsValue === '1.00'

            if (insured && !this.showInsuredPlots) {
              this.showMessage('该地块已隐藏')
              return
            }
            if (!insured && !this.showUninsuredPlots) {
              this.showMessage('该地块已隐藏')
              return
            }

            this.loadFeatureFromGeoServer(feature, shiftPressed)
          } else {
            this.showMessage('该位置没有地块信息')
          }
        } catch (error) {
          this.showMessage('获取地块信息失败')
        }
      }

      this.mapRef.on('singleclick', this.mapClickListener)
    },
    cleanupInteractions () {
      if (this.mapRef && this.drawInteraction) this.mapRef.removeInteraction(this.drawInteraction)
      if (this.mapRef && this.modifyInteraction) this.mapRef.removeInteraction(this.modifyInteraction)
      if (this.mapRef && this.snapInteraction) this.mapRef.removeInteraction(this.snapInteraction)
      if (this.mapRef && this.dragBoxInteraction) this.mapRef.removeInteraction(this.dragBoxInteraction)

      this.drawInteraction = null
      this.modifyInteraction = null
      this.snapInteraction = null
      this.dragBoxInteraction = null
      if (this.helperSource) this.helperSource.clear()
    },
    toggleSnap () {
      if (!this.mapRef) return
      if (this.snapEnabled) {
        const source = this.editSource
        this.snapInteraction = source ? new Snap({ source }) : null
        if (this.snapInteraction) {
          this.mapRef.addInteraction(this.snapInteraction)
        }
      } else if (this.snapInteraction) {
        this.mapRef.removeInteraction(this.snapInteraction)
        this.snapInteraction = null
      }
    },
    startSelect () {
      if (!this.mapRef) return
      this.cleanupInteractions()
      this.mode = 'select'
      this.initInteractions()
      this.setupMapClickListener()
      this.showMessage('点击地图选择地块')
    },
    setupBoxSelectInteraction () {
      if (!this.mapRef || !this.selectInteraction || !this.editSource) return

      this.dragBoxInteraction = new DragBox()

      this.dragBoxInteraction.on('boxstart', (evt) => {
        this.boxSelectAppend = !!evt?.mapBrowserEvent?.originalEvent?.shiftKey
      })

      this.dragBoxInteraction.on('boxend', () => {
        const extent = this.dragBoxInteraction?.getGeometry?.()?.getExtent?.()
        if (!extent) return
        this.applyBoxSelection(extent, this.boxSelectAppend)
      })

      this.mapRef.addInteraction(this.dragBoxInteraction)
    },
    startBoxSelect () {
      if (!this.mapRef) return
      this.cleanupInteractions()
      this.mode = 'box-select'
      this.initInteractions()
      this.setupMapClickListener()
      this.setupBoxSelectInteraction()
      this.showMessage('拖拽框选地块，按住 Shift 可追加选择')
    },
    applyBoxSelection (extent, append = false) {
      if (!this.selectInteraction || !this.editSource || !extent) return

      const selected = this.selectInteraction.getFeatures()
      if (!append) {
        selected.clear()
      }

      const selectedArray = selected.getArray()
      const featuresInExtent = this.editSource.getFeatures().filter((feature) => {
        const geometry = feature?.getGeometry?.()
        return geometry && geometry.intersectsExtent(extent)
      })

      featuresInExtent.forEach((feature) => {
        if (!selectedArray.includes(feature)) {
          selected.push(feature)
        }
      })

      const selectedCount = selected.getLength()
      if (selectedCount === 0) {
        this.currentFeature = null
        this.updateFormFromFeature(null)
        this.showMessage('框选范围内未找到地块')
        return
      }

      if (selectedCount === 1) {
        this.currentFeature = selected.item(0)
        this.updateFormFromFeature(this.currentFeature)
      } else {
        this.currentFeature = null
        this.updateFormFromFeature(null)
      }

      this.showMessage(`已选择 ${selectedCount} 个地块`)
    },
    startDraw () {
      if (!this.mapRef || !this.editSource) return
      this.cleanupInteractions()
      if (this.selectInteraction) this.selectInteraction.getFeatures().clear()

      this.mode = 'add'
      let sketchListenerKey = null
      this.drawInteraction = new Draw({
        source: this.editSource,
        type: 'Polygon'
      })
      this.drawInteraction.on('drawstart', (evt) => {
        const sketch = evt.feature
        sketchListenerKey = sketch.getGeometry().on('change', () => {
          this.form.areaMu = this.calculateAreaMu(sketch.getGeometry())
        })
      })
      this.drawInteraction.on('drawend', (evt) => {
        const feature = evt.feature
        if (sketchListenerKey) {
          unByKey(sketchListenerKey)
          sketchListenerKey = null
        }
        feature.set('source', 'source')
        this.setSelectedFeature(feature)
        this.updateFormFromFeature(feature)
      })
      this.mapRef.addInteraction(this.drawInteraction)
      if (this.snapEnabled) this.toggleSnap()
    },
    startModify () {
      if (!this.mapRef) return
      const selected = this.selectInteraction?.getFeatures()
      if (!selected || selected.getLength() === 0) {
        return this.showMessage('请先选择要修改的地块')
      }
      this.cleanupInteractions()
      this.mode = 'modify'
      this.modifyInteraction = new Modify({ features: selected })
      this.modifyInteraction.on('modifyend', () => {
        this.updateFormFromFeature(this.currentFeature)
      })
      this.mapRef.addInteraction(this.modifyInteraction)
      if (this.snapEnabled) this.toggleSnap()
    },
    async deleteSelected () {
      const selected = this.selectInteraction?.getFeatures()
      if (!selected || selected.getLength() === 0) {
        return this.showMessage('请先选择要删除的地块')
      }
      if (!window.confirm('确定删除该地块？删除后数据不可恢复')) return

      const toRemove = []
      selected.forEach(feature => toRemove.push(feature))

      try {
        for (const feature of toRemove) {
          const plotId = feature.get('id')
          if (plotId) {
            await axios.delete(`/plots/${plotId}`)
          }
          this.editSource.removeFeature(feature)
        }
        selected.clear()
        this.currentFeature = null
        this.resetForm()
        this.refreshPlotLayers()
        this.$emit('plots-changed')
        this.showMessage('地块删除成功')
      } catch (error) {
        const errorMsg = error.response?.data?.detail || '删除失败'
        this.showMessage(errorMsg)
      }
    },
    async mergeSelected () {
      const selected = this.selectInteraction?.getFeatures()
      if (!selected || selected.getLength() < 2) {
        return this.showMessage('请选择两个及以上地块进行合并')
      }

      const plotIds = []
      const features = []
      selected.forEach((feature) => {
        const plotId = feature.get('id')
        if (plotId) {
          plotIds.push(plotId)
          features.push(feature)
        }
      })

      if (plotIds.length < 2) {
        return this.showMessage('请选择已保存的地块进行合并')
      }

      try {
        const response = await axios.post('/plots/merge', plotIds)
        features.forEach(feature => this.editSource.removeFeature(feature))
        selected.clear()
        this.showMessage(response.data.message || '地块合并成功')
        await this.loadPlots()
        this.refreshPlotLayers()
        this.$emit('plots-changed')
      } catch (error) {
        const errorMsg = error.response?.data?.detail || '合并失败'
        this.showMessage(errorMsg)
      }
    },
    startClip () {
      const selected = this.selectInteraction?.getFeatures()
      if (!selected || selected.getLength() !== 1) {
        return this.showMessage('请先选择一个地块进行裁剪')
      }
      const featureToClip = selected.item(0)
      const plotId = featureToClip.get('id')

      if (!plotId) {
        return this.showMessage('请选择已保存的地块进行裁剪')
      }

      this.cleanupInteractions()

      this.mode = 'clip'
      this.helperSource.clear()
      this.drawInteraction = new Draw({
        source: this.helperSource,
        type: 'LineString'
      })
      this.drawInteraction.on('drawend', (evt) => {
        const line = evt.feature.getGeometry()
        this.applyClip(featureToClip, line)
        this.helperSource.clear()
      })
      this.mapRef.addInteraction(this.drawInteraction)
      if (this.snapEnabled) this.toggleSnap()
    },
    async applyClip (feature, lineGeom) {
      const plotId = feature.get('id')
      if (!plotId) {
        return this.showMessage('裁剪失败: 地块未保存')
      }

      try {
        const geojsonFormat = new GeoJSON()
        const lineFeature = new Feature(lineGeom)

        const lineGeoJSON = geojsonFormat.writeFeatureObject(lineFeature, {
          dataProjection: 'EPSG:4326',
          featureProjection: this.mapRef.getView().getProjection()
        })

        const response = await axios.post('/plots/clip', {
          plot_id: plotId,
          line_geometry: lineGeoJSON.geometry
        })

        this.editSource.removeFeature(feature)
        if (this.selectInteraction) {
          this.selectInteraction.getFeatures().clear()
        }

        this.showMessage(response.data.message || '裁剪成功')
        await this.loadPlots()
        this.refreshPlotLayers()
        this.$emit('plots-changed')
      } catch (error) {
        const errorMsg = error.response?.data?.detail || '裁剪失败，请调整裁剪线'
        this.showMessage(errorMsg)
      }
    },
    setSelectedFeature (feature) {
      if (!this.selectInteraction) return
      const selected = this.selectInteraction.getFeatures()
      selected.clear()
      if (feature) selected.push(feature)
      this.currentFeature = feature
    },
    applySelection (feature, shiftPressed = false) {
      if (!feature || !this.selectInteraction) return

      const selectedFeatures = this.selectInteraction.getFeatures()

      if (shiftPressed) {
        const currentId = feature.getId() || feature.get('id')
        const alreadySelected = selectedFeatures.getArray().find(f => {
          const featureId = f.getId() || f.get('id')
          return featureId === currentId
        })

        if (alreadySelected) {
          selectedFeatures.remove(alreadySelected)
        } else {
          selectedFeatures.push(feature)
        }
      } else {
        selectedFeatures.clear()
        selectedFeatures.push(feature)
      }

      if (selectedFeatures.getLength() === 1) {
        this.currentFeature = selectedFeatures.item(0)
        this.updateFormFromFeature(this.currentFeature)
      } else {
        this.currentFeature = null
        this.updateFormFromFeature(null)
      }
    },
    updateFormFromFeature (feature) {
      if (!feature) {
        this.resetForm()
        return
      }
      this.form.plotName = feature.get('land_name') || feature.get('plotName') || ''
      this.form.ownerName = feature.get('owner_name') || feature.get('qsrmc') || feature.get('ownerName') || ''
      this.form.idCard = feature.get('owner_id_card') || feature.get('qsrid') || ''
      this.form.remark = feature.get('remark') || feature.get('notes') || ''
      this.form.plotCode = feature.get('external_plot_code') || ''
      this.form.areaMu = this.calculateAreaMu(feature.getGeometry())
    },
    calculateAreaMu (geometry) {
      if (!geometry) return ''
      const geojson = new GeoJSON()
      const feature = new Feature(geometry)
      const polygon = geojson.writeFeatureObject(feature)
      const area = turf.area(polygon)
      return (area / 666.6667).toFixed(2)
    },
    async savePlot () {
      if (!this.currentFeature) {
        return this.showMessage('请先选择或绘制地块')
      }
      if (!this.form.plotName || !this.form.ownerName) {
        return this.showMessage('请填写必填信息')
      }

      const geom = this.currentFeature.getGeometry()
      const geojson = new GeoJSON()
      const geomFeature = new Feature(geom)

      const geomObject = geojson.writeFeatureObject(geomFeature, {
        dataProjection: 'EPSG:4326',
        featureProjection: this.mapRef.getView().getProjection()
      })

      if (!geomObject?.geometry) {
        return this.showMessage('地块几何无效')
      }

      const plotId = this.currentFeature.get('id')
      const villageCode = this.$store.state.user?.bound_region_code || this.selectedArea?.place_id
      const featureLandType = String(this.currentFeature.get('land_type') ?? this.currentFeature.get('DL') ?? '').trim()
      const normalizedLandType = (featureLandType === '42' || featureLandType === '水地') ? '42' : '41'

      if (!villageCode) {
        return this.showMessage('无法获取区域代码')
      }

      try {
        if (plotId) {
          const response = await axios.put(`/plots/${plotId}`, {
            geometry: geomObject.geometry,
            land_name: this.form.plotName,
            land_type: normalizedLandType,
            owner_name: this.form.ownerName,
            owner_id_card: this.form.idCard,
            notes: this.form.remark || null
          })
          this.showMessage(response.data.message || '地块更新成功')
        } else {
          const response = await axios.post('/plots/', {
            geometry: geomObject.geometry,
            land_name: this.form.plotName,
            land_type: normalizedLandType,
            owner_name: this.form.ownerName,
            owner_id_card: this.form.idCard,
            village_code: villageCode,
            notes: this.form.remark || null
          })
          this.currentFeature.set('id', response.data.id)
          this.currentFeature.set('external_plot_code', response.data.external_plot_code)
          this.showMessage(response.data.message || '地块创建成功')
        }

        this.currentFeature.set('land_name', this.form.plotName)
        this.currentFeature.set('land_type', normalizedLandType)
        this.currentFeature.set('owner_name', this.form.ownerName)
        this.currentFeature.set('owner_id_card', this.form.idCard)
        this.currentFeature.set('notes', this.form.remark || '')

        this.refreshPlotLayers()
        this.$emit('plots-changed')
      } catch (error) {
        const errorMsg = error.response?.data?.detail || '保存失败'
        this.showMessage(errorMsg)
      }
    },
    refreshPlotLayers () {
      if (!this.mapRef) return
      const plotLayerIds = ['wms-plot-layer']
      const layers = this.mapRef.getLayers().getArray()

      plotLayerIds.forEach(lid => {
        const layer = layers.find(l => l.get('lid') === lid)
        if (layer && layer.getSource) {
          const source = layer.getSource()
          if (source.updateParams) {
            source.updateParams({ _t: Date.now() })
          }
          if (source.refresh) {
            source.refresh()
          }
          if (!layer.getVisible()) {
            layer.setVisible(true)
          }
        }
      })
    },
    cancelEdit () {
      this.setSelectedFeature(null)
      this.cleanupInteractions()
      this.mode = 'idle'
    },
    resetForm () {
      this.form.plotName = ''
      this.form.ownerName = ''
      this.form.idCard = ''
      this.form.remark = ''
      this.form.areaMu = ''
      this.form.plotCode = ''
    },
    showMessage (text) {
      this.snackbarText = text
      this.snackbar = true
    },
    loadFeatureFromGeoServer (geoJsonFeature, shiftPressed = false) {
      if (!geoJsonFeature || !geoJsonFeature.geometry) {
        return
      }

      try {
        const format = new GeoJSON()

        const olFeature = format.readFeature(geoJsonFeature, {
          dataProjection: 'EPSG:4326',
          featureProjection: this.mapRef.getView().getProjection()
        })

        const rawFeatureId = olFeature.getId() || olFeature.get('id') || geoJsonFeature.id
        let numericId = null

        if (rawFeatureId) {
          if (typeof rawFeatureId === 'string' && rawFeatureId.includes('.')) {
            const parts = rawFeatureId.split('.')
            numericId = parseInt(parts[parts.length - 1], 10)
          } else {
            numericId = parseInt(rawFeatureId, 10)
          }
        }

        const existingFeatures = this.editSource.getFeatures()
        const existingFeature = existingFeatures.find(f => {
          const fId = f.getId() || f.get('id')
          const fNumericId = typeof fId === 'string' && fId.includes('.')
            ? parseInt(fId.split('.').pop(), 10)
            : parseInt(fId, 10)
          return !isNaN(fNumericId) && fNumericId === numericId
        })

        if (existingFeature) {
          existingFeature.setGeometry(olFeature.getGeometry())
          const properties = olFeature.getProperties()
          Object.keys(properties).forEach(key => {
            if (key !== 'geometry') {
              existingFeature.set(key, properties[key])
            }
          })
          this.currentFeature = existingFeature
        } else {
          if (!isNaN(numericId)) {
            olFeature.setId(numericId)
            olFeature.set('id', numericId)
          }

          this.editSource.addFeature(olFeature)
          this.currentFeature = olFeature
        }

        this.applySelection(this.currentFeature, shiftPressed)
      } catch (error) {
        this.showMessage('加载地块失败')
      }
    }
  }
}
</script>

<style scoped>
.plot-draw-panel {
  min-height: 100%;
}

.plot-tool-section {
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  padding: 10px;
  background: #ffffff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: box-shadow 0.3s ease;
}

.plot-tool-section:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
}

.tool-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}

.tool-btn {
  width: 100%;
  min-width: 40px !important;
  height: 40px;
}

@media (max-width: 960px) {
  .tool-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>
